import { Outline as Info } from '@manomano-internal/tamagoshi-icons/esm/material/Info';
import { Outline as Caret } from '@manomano-internal/tamagoshi-icons/esm/material/KeyboardArrowRight';
import classnames from 'classnames';
import type { FC } from 'react';
import { Text } from '@/core/tamagoshi/Text/Text';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { translate, translateFormatter } from '@/domains/core/localization/translate';
import { Link } from '@/domains/core/routing/components/Link';
import { ResponsivePopover } from '@/domains/core/tamagoshi/ResponsivePopover/ResponsivePopover';
import { H3 } from '@/domains/core/tamagoshiv2/components/Typography/Typography';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import type { MdScore } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { DoughnutScore } from '@/domains/productDiscovery/ArticleReviews/components/DoughnutScore/DoughnutScore';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';
import styles from './mdScores.module.scss';
const t = translate({
  "averageCustomerRatings": "Average customer ratings",
  "top": "Top",
  "reviews": "reviews",
  "seeTopRated": "See the top-rated products"
});
const rankingPositionTranslation = translateFormatter<{
  position: number;
}>([{
  "type": 0,
  "value": "N\xBA "
}, {
  "type": 1,
  "value": "position"
}, {
  "type": 0,
  "value": " in its category"
}], undefined, undefined, "en");
export interface MdScoresProps {
  data: MdScore[];
  className?: string;
}
const sendMdCTAClickEvent = (mdName: string, ctaButtonPosition: number) => {
  Gtm.push({
    event: 'interaction_click on MD ranking',
    e_category: 'navigation',
    e_action: 'Click on MD ranking',
    e_label: undefined,
    event_trigger: 'review-section',
    amp_event_name: 'Click on MD ranking',
    md_ranking_label: mdName,
    md_ranking_position: ctaButtonPosition + 1,
    page_type: 'product',
    interaction_hit: 'False'
  });
};
export const SingleMdScoreCTA: FC<{
  mdr: MdScore;
  ctaButtonPosition: number;
}> = ({
  mdr,
  ctaButtonPosition
}) => {
  const [isProductPageRevampEnabled] = useBooleanFlags([StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP]);
  const href = isProductPageRevampEnabled ? `#sidePanel-ranking-${mdr.questionId}` : `#ranking-${mdr.questionId}`;
  return <Link className={classnames(styles.mdr, styles.cta)} href={href} shallow onClick={() => sendMdCTAClickEvent(mdr.name, ctaButtonPosition)} data-testid={`link-for-mdr-${mdr.questionId}`}>
      <div>
        <DoughnutScore score={mdr.avgRating} />
      </div>
      <div className={styles.mdrText}>
        <Text className={styles.mdrName}>
          {mdr.name} &nbsp;
          <span className={styles.mdrReviewsTotal}>{`(${mdr.nbTotal} ${t.reviews})`}</span>
        </Text>
        <Text className={styles.seeTopRanking}>
          {mdr.rankingPosition ? rankingPositionTranslation.format({
          position: mdr.rankingPosition
        }) : t.seeTopRated}
        </Text>
      </div>
      <div>
        <Caret className={styles.caret} />
      </div>
    </Link>;
};
SingleMdScoreCTA.displayName = 'SingleMdScoreCTA';
export const SingleMdScoreNoCTA: FC<{
  mdr: MdScore;
}> = ({
  mdr
}) => <div className={styles.mdr}>
    <div>
      <DoughnutScore score={mdr.avgRating} />
    </div>
    <div className={styles.mdrText}>
      <Text className={styles.mdrName}>
        {mdr.name} &nbsp;
        <span className={styles.mdrReviewsTotal}>{`(${mdr.nbTotal} ${t.reviews})`}</span>
        <ResponsivePopover title={mdr.name} text={mdr.description} trigger={<Info className={styles.infoIcon} />} id={mdr.name} />
      </Text>
    </div>
  </div>;
SingleMdScoreNoCTA.displayName = 'SingleMdScoreNoCTA';
export const SingleMdScore: FC<{
  mdr: MdScore;
  ctaButtonPosition: number;
}> = ({
  mdr,
  ctaButtonPosition
}) => mdr.hasRanking ? <SingleMdScoreCTA mdr={mdr} ctaButtonPosition={ctaButtonPosition} /> : <SingleMdScoreNoCTA mdr={mdr} />;
export const MdScores: FC<MdScoresProps> = ({
  data,
  className
}) => <div className={classnames(className, styles.mdrsWrapper)}>
    <H3 className={styles.mdrsSectionTitle}>{t.averageCustomerRatings}</H3>
    <div className={styles.mdrsGrid}>
      {data?.map((mdr: MdScore, index: number) => <SingleMdScore mdr={mdr} key={mdr.questionId} ctaButtonPosition={index} />)}
    </div>
  </div>;
SingleMdScore.displayName = 'SingleMdScore';